<template>
  <!--
    Globálny náhľad súboru (useNahladSuboru): prekryje hlavnú plochu aplikácie, ľavý panel a bočný panel
    s diskusiou ostanú viditeľné. Esc alebo „Späť“ ho zavrie, pri prechode na inú stránku sa zavrie sám.
  -->
  <transition name="nahlad">
    <section
      v-if="nahlad.otvoreny"
      class="nahlad"
      :class="{ 'nahlad--mobile': $vuetify.display.mobile }"
      role="region"
      :aria-label="`Náhľad súboru ${nahlad.nazov}`"
    >
      <header class="nahlad__bar">
        <v-btn variant="text" size="small" prepend-icon="mdi-arrow-left" @click="zavriet">Späť</v-btn>
        <span class="nahlad__tile" aria-hidden="true">
          <v-icon icon="mdi-file-document-outline" size="18" />
        </span>
        <div class="nahlad__nazov">
          <div class="nahlad__file">{{ nahlad.nazov }}</div>
          <div v-if="nahlad.podtitul" class="nahlad__meta">{{ nahlad.podtitul }}</div>
        </div>
        <v-spacer></v-spacer>
        <v-btn
          v-if="nahlad.stiahnut"
          variant="outlined"
          size="small"
          prepend-icon="mdi-download-outline"
          :loading="stahujem"
          @click="stiahnut"
        >
          Stiahnuť
        </v-btn>
        <v-btn icon variant="text" size="small" aria-label="Zavrieť náhľad" @click="zavriet">
          <v-icon icon="mdi-close" size="20" />
        </v-btn>
      </header>

      <div v-if="chyba" class="nahlad__chyba">
        <v-icon icon="mdi-alert-circle-outline" size="40" />
        <span>{{ chyba }}</span>
        <v-btn v-if="nahlad.stiahnut" variant="outlined" size="small" prepend-icon="mdi-download-outline" @click="stiahnut">
          Stiahnuť súbor
        </v-btn>
      </div>
      <DokumentViewer
        v-else
        ref="viewer"
        vyplnit
        :blob="blob"
        :nazov="nahlad.nazov"
        :komentovanie="!!nahlad.komentovat"
        @komentovat="(vyber) => nahlad.komentovat(vyber)"
        @vykreslene="onVykreslene"
      />

      <p v-if="nahlad.komentovat && !chyba" class="nahlad__hint">
        Označte text v dokumente a kliknite na „Komentovať výber“ — pripomienka sa uloží s odkazom na toto miesto.
      </p>
    </section>
  </transition>
</template>

<script>
import DokumentViewer from './DokumentViewer.vue';
import { nahlad, zavrietNahlad } from '@/composables/useNahladSuboru';
import { useNotificationStore } from '@/store/notification';

export default {
  name: 'NahladSuboru',

  components: { DokumentViewer },

  setup() {
    return { nahlad };
  },

  data() {
    return {
      blob: null,
      chyba: '',
      stahujem: false,
      requestId: 0
    };
  },

  watch: {
    // Nový súbor (aj keď je náhľad už otvorený)
    'nahlad.verzia'() {
      if (nahlad.otvoreny) this.nacitat();
    },

    'nahlad.otvoreny'(otvoreny) {
      if (!otvoreny) {
        this.requestId += 1;
        this.blob = null;
        this.chyba = '';
      }
    },

    // Prechod na inú stránku náhľad zavrie
    '$route.path'() {
      if (nahlad.otvoreny) zavrietNahlad();
    }
  },

  mounted() {
    window.addEventListener('keydown', this.onKeydown);
  },

  beforeUnmount() {
    window.removeEventListener('keydown', this.onKeydown);
  },

  methods: {
    async nacitat() {
      const id = ++this.requestId;
      this.blob = null;
      this.chyba = '';
      try {
        const blob = await nahlad.nacitaj();
        if (id !== this.requestId) return;
        this.blob = blob instanceof Blob ? blob : new Blob([blob]);
      } catch (error) {
        if (id !== this.requestId) return;
        console.error('Chyba pri načítaní súboru na náhľad:', error);
        this.chyba = error.response && error.response.status === 404
          ? 'Súbor sa nenašiel. Mohol byť medzičasom vymazaný.'
          : 'Súbor sa nepodarilo načítať.';
      }
    },

    async onVykreslene() {
      const ciel = nahlad.zvyraznenie;
      if (!ciel || !ciel.citacia || !this.$refs.viewer) return;
      nahlad.zvyraznenie = null;
      const najdene = await this.$refs.viewer.zvyraznit(ciel.citacia, ciel.strana);
      if (!najdene) useNotificationStore().info('Citovaný text sa v súbore nenašiel.');
    },

    // Zvýraznenie v už otvorenom súbore (napr. ďalší citát z diskusie)
    async zvyraznit(citacia, strana) {
      if (this.$refs.viewer) return this.$refs.viewer.zvyraznit(citacia, strana);
      return false;
    },

    async stiahnut() {
      if (!nahlad.stiahnut) return;
      this.stahujem = true;
      try {
        await nahlad.stiahnut();
      } catch (error) {
        console.error('Chyba pri sťahovaní súboru:', error);
        useNotificationStore().error('Súbor sa nepodarilo stiahnuť');
      } finally {
        this.stahujem = false;
      }
    },

    zavriet() {
      zavrietNahlad();
    },

    onKeydown(event) {
      if (event.key !== 'Escape' || !nahlad.otvoreny) return;
      // Esc v otvorenom dialógu patrí dialógu
      if (document.querySelector('.v-overlay--active.v-dialog')) return;
      zavrietNahlad();
    }
  }
};
</script>

<style scoped>
.nahlad {
  position: absolute;
  inset: 0;
  z-index: 20;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: var(--surface);
}

.nahlad--mobile {
  position: fixed;
  z-index: 1005;
}

.nahlad__bar {
  display: flex;
  flex: none;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  padding: 10px 14px 10px 8px;
  border-bottom: 1px solid var(--border-subtle);
}

.nahlad__tile {
  display: grid;
  flex: none;
  place-items: center;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background: var(--surface-pearl);
  color: var(--text-secondary);
}

.nahlad__nazov {
  min-width: 0;
}

.nahlad__file {
  overflow: hidden;
  max-width: min(520px, 60vw);
  font-size: 0.9375rem;
  font-weight: 650;
  color: var(--text-primary);
  white-space: nowrap;
  text-overflow: ellipsis;
}

.nahlad__meta {
  font-size: 0.8125rem;
  color: var(--text-muted);
}

.nahlad__chyba {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px;
  background: var(--surface-soft);
  font-size: 0.9375rem;
  color: var(--text-muted);
  text-align: center;
}

.nahlad__hint {
  flex: none;
  margin: 0;
  padding: 10px 16px;
  border-top: 1px solid var(--border-subtle);
  font-size: 0.8125rem;
  color: var(--text-muted);
}

.nahlad-enter-active,
.nahlad-leave-active {
  transition: opacity var(--duration-fast) ease, transform var(--duration-fast) ease;
}

.nahlad-enter-from,
.nahlad-leave-to {
  opacity: 0;
  transform: translateY(6px);
}

@media (prefers-reduced-motion: reduce) {
  .nahlad-enter-active,
  .nahlad-leave-active {
    transition: none;
  }
}
</style>
